import { FC, useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Loader } from '@prismamedia/one-components';
import { BrandKey } from '../../__generated__/queries-web';
import { useOpenAiPrompts } from '../../utils/useOpenAiPrompts';
import { callOpenAI } from '../../utils/openAI';
import {
  createGenerateOptions,
  createRetryOptions,
  parseTitles,
} from './utils';
import { MultipleSuggestions } from './MultipleSuggestions';
import { TextSuggestionMenu } from './TextSuggestionMenu/index';
import ReactGA from 'react-ga4';

interface TextSuggestionProps {
  anchorRef: HTMLDivElement | null;
  brandKey: BrandKey;
  className?: string;
  handleClose: () => void;
  fieldValue: string | null;
  field: string;
  title: string;
  body?: string;
  lead?: string;
  publishedAt?: string;
  onConfirm: (suggestion: string) => void;
}

// Types moved to utils.tsx where they're actually used

export const TextSuggestion: FC<TextSuggestionProps> = ({
  anchorRef,
  brandKey,
  handleClose,
  fieldValue,
  field,
  title,
  body,
  lead,
  publishedAt,
  onConfirm,
}) => {
  const [multipleSuggestions, setMultipleSuggestions] = useState<string[]>([]);
  const [showMultipleSuggestions, setShowMultipleSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const menuKey = fieldValue ? 'edit' : 'new';

  const handleOpenAi = async (
    content: string,
    maxTokens?: number,
    temperature?: number,
  ) => {
    const openAiResponse = await callOpenAI(content, maxTokens, temperature);
    return openAiResponse;
  };

  const { generateMenu, retryMenu, maxTokens, temperature } = useOpenAiPrompts({
    brandKey,
    body,
    lead,
    title,
    fieldValue: fieldValue || '',
    field,
    publishedAt,
  });

  const onGenerate = useCallback(
    async (content: string) => {
      setIsLoading(true);
      try {
        ReactGA.event({
          category: 'Article IA Titre accroche',
          action: 'Article IA Generation',
          label: window.location.href,
        });
        const result = await handleOpenAi(content, maxTokens, temperature);
        const responseContent = result.data?.executeTextPrompt?.content || '';

        const suggestions = parseTitles(responseContent);
        setMultipleSuggestions(suggestions);
        setShowMultipleSuggestions(true);
      } finally {
        setIsLoading(false);
      }
    },
    [maxTokens, temperature],
  );

  const generateOptions = createGenerateOptions(generateMenu, onGenerate);

  const onRetry = useCallback((suggestions: string[]) => {
    setMultipleSuggestions(suggestions);
    setShowMultipleSuggestions(true);
  }, []);

  const retryOptions = createRetryOptions(
    retryMenu,
    fieldValue || '',
    handleOpenAi,
    onRetry,
    setIsLoading,
    maxTokens,
    temperature,
  );

  const menu = {
    new: generateOptions,
    edit: [...generateOptions, ...retryOptions],
    confirm: [], // No longer needed since we always show multiple suggestions
  };

  const handleSuggestionSelect = useCallback(
    (selectedSuggestion: string) => {
      setShowMultipleSuggestions(false);
      setMultipleSuggestions([]);
      // Automatically fill the field with the selected suggestion
      onConfirm(selectedSuggestion);
    },
    [onConfirm],
  );

  const handleCloseMultipleSuggestions = useCallback(() => {
    setShowMultipleSuggestions(false);
    setMultipleSuggestions([]);
  }, []);

  return (
    <>
      {!showMultipleSuggestions && !isLoading && (
        <TextSuggestionMenu
          anchorRef={anchorRef}
          handleClose={handleClose}
          menuItems={menu}
          menuKey={menuKey}
        />
      )}
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: anchorRef
              ? anchorRef.getBoundingClientRect().bottom + window.scrollY
              : '50%',
            left: anchorRef ? anchorRef.getBoundingClientRect().left : '50%',
            width: anchorRef ? anchorRef.getBoundingClientRect().width : 'auto',
            transform: anchorRef ? 'none' : 'translate(-50%, -50%)',
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            padding: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Loader color="warning" thickness={5} size={20} />
          <Typography variant="body2" color="text.secondary">
            Génération en cours...
          </Typography>
        </Box>
      )}
      {showMultipleSuggestions && (
        <MultipleSuggestions
          suggestions={multipleSuggestions}
          field={field}
          onSelect={handleSuggestionSelect}
          onClose={handleCloseMultipleSuggestions}
        />
      )}
    </>
  );
};
